import { update as updated } from '@app/utils/update';
import { ActionHandler, Modals } from '@typings';
import { update } from 'space-lift';
import { createReducer, EmptyAction } from 'typesafe-actions';

import {
  copyToNewSelectionFailure,
  copyToNewSelectionRequest,
  copyToNewSelectionSuccess,
  createSelection,
  createSelectionSuccess,
  editSelection,
  editSelectionSuccess,
  setItemsFailure,
  setItemsRequest,
  setItemsSuccess,
  setMultipleItemsRequest,
  setPrepacksRequest,
} from '../order';

import * as actions from './actions';
import { setLookbookProductsGridSize } from './actions';

export interface UIReducer {
  isMatrixTotalsColumnPinned: boolean;
  isOffline: boolean;
  isOrderSettingsFormDisabled: boolean;
  isSaving: boolean;
  shouldShowMoreShippingAddresses: boolean;
  isSoftLogoutMode: boolean;
  isProductsGridView: boolean;
  lookbookProductsGridSize: 'small' | 'medium' | 'large';
  productsGridSize: 'small' | 'medium' | 'large';
  visibleModals: Modals.Type[];
  languageToSet?: string;
  isMenuOpen: boolean;
}

const initialState: UIReducer = {
  isMatrixTotalsColumnPinned: true,
  isMenuOpen: false,
  isOffline: false,
  isOrderSettingsFormDisabled: false,
  isProductsGridView: true,
  isSaving: false,
  isSoftLogoutMode: false,
  lookbookProductsGridSize: 'medium',
  productsGridSize: 'medium',
  shouldShowMoreShippingAddresses: false,
  visibleModals: [],
};

export { initialState as initialUIState };

const handleHideModal: ActionHandler<UIReducer, typeof actions.hideModal> = (state, action) => {
  return updated(state, {
    visibleModals: state.visibleModals.filter(modal => modal !== action.payload),
  });
};

const handleShowModal: ActionHandler<UIReducer, typeof actions.showModal> = (state, action) => {
  return updated(state, {
    visibleModals: [...state.visibleModals, ...(state.visibleModals.includes(action.payload) ? [] : [action.payload])],
  });
};

const handleToggleConnectivityStatus: ActionHandler<UIReducer, typeof actions.toggleNetworkConnectivity> = (state, action) =>
  updated(state, {
    isOffline: action.payload.isOffline,
  });

const handleSetItemAmount: ActionHandler<UIReducer, EmptyAction<string>> = state =>
  update(state, {
    isSaving: true,
  });

const handleSetItemAmountSuccess: ActionHandler<UIReducer, typeof setItemsSuccess> = state =>
  updated(state, {
    isSaving: false,
  });

const handleSetItemAmountFailure: ActionHandler<UIReducer, typeof setItemsFailure> = state =>
  updated(state, {
    isSaving: false,
  });

const handleEditSelection: ActionHandler<UIReducer, typeof editSelection> = state =>
  updated(state, {
    isOrderSettingsFormDisabled: true,
  });

const handleEditSelectionSuccess: ActionHandler<UIReducer, typeof editSelectionSuccess> = state => {
  return updated(state, {
    isOrderSettingsFormDisabled: false,
    visibleModals: state.visibleModals.filter(modal => modal !== 'EditOrderModal'),
  });
};

const handleCopyToNewSelection: ActionHandler<UIReducer, typeof copyToNewSelectionRequest> = state =>
  updated(state, {
    isOrderSettingsFormDisabled: true,
  });

const handleCopyToNewSelectionFailure: ActionHandler<UIReducer, typeof copyToNewSelectionFailure> = state =>
  updated(state, {
    isOrderSettingsFormDisabled: false,
  });

const handleCopyToNewSelectionSuccess: ActionHandler<UIReducer, typeof copyToNewSelectionSuccess> = state => {
  return updated(state, {
    isOrderSettingsFormDisabled: false,
  });
};

const handleCreateSelection: ActionHandler<UIReducer, typeof createSelection> = state =>
  updated(state, {
    isOrderSettingsFormDisabled: true,
  });

const handleCreateSelectionSuccess: ActionHandler<UIReducer, typeof createSelectionSuccess> = state => {
  return updated(state, {
    isOrderSettingsFormDisabled: false,
    visibleModals: state.visibleModals.filter(modal => modal !== 'CreateOrderModal'),
  });
};

const handleSetProductsGridView: ActionHandler<UIReducer, typeof actions.setProductsGridView> = state =>
  updated(state, { isProductsGridView: true });

const handleSetProductsListView: ActionHandler<UIReducer, typeof actions.setProductsListView> = state =>
  updated(state, { isProductsGridView: false });

const handleSetSoftLogout: ActionHandler<UIReducer, typeof actions.setSoftLogoutMode> = state =>
  update(state, {
    isSoftLogoutMode: true,
  });

const handleSetShouldShowMoreShippingAddresses: ActionHandler<UIReducer, typeof actions.setShouldShowMoreShippingAddresses> = (
  state,
  action,
) =>
  update(state, {
    shouldShowMoreShippingAddresses: action.payload,
  });

const handleRequestLanguageChange: ActionHandler<UIReducer, typeof actions.requestLanguageChange> = (state, action) =>
  update(state, {
    languageToSet: action.payload,
  });

const handleSetMatrixTotalsColumnPinned: ActionHandler<UIReducer, typeof actions.setMatrixTotalsColumnPinned> = (state, action) =>
  update(state, {
    isMatrixTotalsColumnPinned: action.payload,
  });

const handleSetProductsGridSize: ActionHandler<UIReducer, typeof actions.setProductsGridSize> = (state, action) =>
  update(state, {
    productsGridSize: action.payload,
  });

const handleSetIsMenuOpen: ActionHandler<UIReducer, typeof actions.setIsMenuOpen> = (state, action) => {
  return update(state, {
    isMenuOpen: action.payload,
  });
};
const handleSetLookbookProductsGridSize: ActionHandler<UIReducer, typeof setLookbookProductsGridSize> = (state, action) =>
  update(state, {
    lookbookProductsGridSize: action.payload,
  });

export default createReducer<UIReducer>(initialState)
  .handleAction(actions.hideModal, handleHideModal)
  .handleAction(actions.showModal, handleShowModal)
  .handleAction(actions.toggleNetworkConnectivity, handleToggleConnectivityStatus)
  .handleAction(actions.setProductsGridView, handleSetProductsGridView)
  .handleAction(actions.setProductsListView, handleSetProductsListView)
  .handleAction(actions.setProductsGridSize, handleSetProductsGridSize)
  .handleAction(setItemsRequest, handleSetItemAmount)
  .handleAction(setMultipleItemsRequest, handleSetItemAmount)
  .handleAction(setPrepacksRequest, handleSetItemAmount)
  .handleAction(setItemsSuccess, handleSetItemAmountSuccess)
  .handleAction(setItemsFailure, handleSetItemAmountFailure)
  .handleAction(setItemsFailure, handleSetItemAmountFailure)
  .handleAction(editSelection, handleEditSelection)
  .handleAction(editSelectionSuccess, handleEditSelectionSuccess)
  .handleAction(copyToNewSelectionRequest, handleCopyToNewSelection)
  .handleAction(copyToNewSelectionSuccess, handleCopyToNewSelectionSuccess)
  .handleAction(copyToNewSelectionFailure, handleCopyToNewSelectionFailure)
  .handleAction(createSelection, handleCreateSelection)
  .handleAction(createSelectionSuccess, handleCreateSelectionSuccess)
  .handleAction(actions.setSoftLogoutMode, handleSetSoftLogout)
  .handleAction(actions.setShouldShowMoreShippingAddresses, handleSetShouldShowMoreShippingAddresses)
  .handleAction(actions.requestLanguageChange, handleRequestLanguageChange)
  .handleAction(actions.setMatrixTotalsColumnPinned, handleSetMatrixTotalsColumnPinned)
  .handleAction(actions.setIsMenuOpen, handleSetIsMenuOpen)
  .handleAction(setLookbookProductsGridSize, handleSetLookbookProductsGridSize);
